<template>
  <SidePanel importStyle="App" ctaTitle="Submit" :ctaAction="handleSubmit"
    :ctaActionDisabled="ratingError || imageError || titleError || contentError || showAlert" :closeModal="onClose"
    :helperText="true">
    <template #modalTitle>
      <div :class="`${classVariableName}-panel-title`">
        Ratings & Reviews
      </div>
    </template>

    <template #default>
      <StarRating :class="`${classVariableName}-rating-container-bg-box`" :readonly="showAlert" @updateRating="(event) => {
          rating = event;
          ratingError = null;
        }" :allowFraction="parseInt(ratingValidations.step) !== ratingValidations.step" :initialRating="rating"
        size="l" :showHelperText="true" starBaseColor="#FCF2EF" />

      <div :class="`${classVariableName}-panel-body-section`">
        <div :class="`${classVariableName}-panel-sub-section`">
          <div :class="`${classVariableName}-review-add-main-text`">
            Help us with your review
          </div>

          <div :class="`${classVariableName}-review-add-help-text`">
            Your feedback is incredibly important to us and can help others make informed decisions too. For delivery or
            packaging related feedback, please visit our Contact Us page
          </div>
        </div>

        <div :class="`${classVariableName}-panel-sub-section ${classVariableName}-review-anonymous`">
          <div :class="`${classVariableName}-review-anonymous-header`">
            Post review anonymously
            <div :class="`${classVariableName}-review-anonymous-toggler`">
              <label :class="`${classVariableName}-review-switch`">
                <input :checked="anonymous" type="checkbox" @change="anonymous = !anonymous" />
                <span :class="`${classVariableName}-review-slider round`"></span>
              </label>
            </div>
          </div>
          <div v-if="!anonymous" :class="`${classVariableName}-review-anonymous-divider`"></div>
          <div v-if="!anonymous" :class="`${classVariableName}-review-anonymous-footer`">
            {{ userName }}
            <CertifiedBuyer kind="primary" />
            <div :class="`${classVariableName}-review-anonymous-time`">
              {{ formatDate(new Date()) }}
            </div>
          </div>
        </div>

        <div v-if="titleValidations && !titleValidations.hidden" :class="`${classVariableName}-panel-sub-section`">
          <div :class="`${classVariableName}-panel-input-label ${
              titleError ? `${classVariableName}-panel-input-label-error` : ''
            }`">
            Review title {{ titleValidations.required || !isEmpty(titleError) ? "*" : "" }}
          </div>
          <Input :value="title" @updateInput="(event) => {
              title = event;
              titleError = null;
            }" @hasError="(event) => {
              titleError = event;
            }" :maxCharCount="titleValidations.maxCharLimit" :minCharCount="titleValidations.minCharLimit"
            placeholder="What's most important to know?" :readOnly="showAlert" :childClass="`${
              titleError
                ? `${classVariableName}-input-label-error-border`
                : ''
            }`" />
        </div>

        <div v-if="contentValidations && !contentValidations.hidden" :class="`${classVariableName}-panel-sub-section`">
          <div :class="`${classVariableName}-panel-input-label ${
              contentError ? `${classVariableName}-panel-input-label-error` : ''
            }`">
            Review description {{ contentValidations.required || !isEmpty(contentError) ? "*" : "" }}
          </div>
          <Textarea :value="content" @updateInput="(event) => {
              content = event;
              contentError = null;
            }" @hasError="(event) => {
              contentError = event;
            }" :maxCharCount="contentValidations.maxCharLimit" :minCharCount="contentValidations.minCharLimit"
            placeholder="What did you think of this product? Tell us what you liked or disliked about it"
            :readOnly="showAlert" :rows="8" :childClass="`${
              contentError
                ? `${classVariableName}-input-label-error-border`
                : ''
            }`" />
        </div>

        <div v-if="imageValidations && !imageValidations.hidden" :class="`${classVariableName}-panel-sub-section`">
          <div :class="`${classVariableName}-d-flex ${classVariableName}-flex-wrap ${classVariableName}-gap-12`">
            <div v-if="!isEmpty(images)"
              :class="`${classVariableName}-d-flex ${classVariableName}-flex-wrap ${classVariableName}-gap-12`">
              <div v-for="(img, index) in images" :key="index" :class="`${classVariableName}-img-container`">
                <div :class="`${classVariableName}-closable`" @click="() => removeImage(index)">
                  x
                </div>
                <img :key="index" :src="getImage(img)" alt="image preview"
                  :class="`${classVariableName}-image ${classVariableName}-d-flex`" />
              </div>
            </div>
            <div v-if="isEmpty(images) || images.length < imageValidations.countLimit"
              :class="`${classVariableName}-image-upload-container`">
              <input id="imgInput" type="file" multiple title="Upload Images..." :style="{
                  width: '100%',
                  position: 'absolute',
                  opacity: '0',
                  height: '100%',
                  color: 'transparent',
                  cursor: 'pointer'
                }" :disabled="showAlert" @change="handleImagePreview" accept=".jpg, .png, .jpeg" />
              <ImageUploadSVG :size="isEmpty(images) ? '48' : '60'" />
              <div v-if="isEmpty(images)"
                :class="`${classVariableName}-d-flex ${classVariableName}-flex-column ${classVariableName}-align-items-stretch ${classVariableName}-justify-content-center`">
                <div :class="`${classVariableName}-review-image-main-text`">
                  Add photos to your review
                </div>
              </div>
            </div>
          </div>

          <div v-if="imageError" :class="`${classVariableName}-panel-input-label-error-text `">
            {{ imageError }}
          </div>
        </div>
      </div>
    </template>
  </SidePanel>
</template>

<script>
import { defineComponent } from "@vue/composition-api";
import { isEmpty, classVariableName, formatDate, emitEvent, getEventData } from "../utils";
import CUSTOM_EVENTS from "../constants";
import StarRating from "./common/StarRating.vue";
import Input from "./common/Input.vue";
import Textarea from "./common/Textarea.vue";
import SidePanel from "./common/SidePanel.vue";
import CertifiedBuyer from "./common/CertifiedBuyer.vue";
import ImageUploadSVG from "./common/ImageUploadSVG.vue";
export default defineComponent({
  name: "AddReviewSidePanel",
  components: {
    Input,
    Textarea,
    StarRating,
    SidePanel,
    CertifiedBuyer,
    ImageUploadSVG,
  },
  props: {
    product: Object,
    prdImgURL: {
      type: String,
      default: "",
    },
    prdName: {
      type: String,
      default: "",
    },
    showAlert: {
      type: Boolean,
      default: false,
    },
    initialRating: {
      type: Number,
      default: 0.0,
    },
    initialReview: {
      type: Object,
      default: null,
    },
    validationConfig: {
      type: Object,
      default: {},
    },
    onClose: Function,
    onSubmit: Function,
    userName: String,
  },
  mounted() { emitEvent(CUSTOM_EVENTS.WRITE_REVIEW_SCREEN, getEventData(true, 'PDP', this.product, { rating: this.rating, anonymous: this.anonymous })); },
  setup() {},
  data() {
    return {
      anonymous: false,
      titleValidations: this.validationConfig && this.validationConfig.title,
      contentValidations: this.validationConfig && this.validationConfig.content,
      ratingValidations: this.validationConfig && this.validationConfig.rating,
      imageValidations: this.validationConfig && this.validationConfig.image,
      title: this.initialReview && this.initialReview.title,
      content: this.initialReview && this.initialReview.content,
      images: (this.initialReview && this.initialReview.images) || undefined,
      rating: this.initialRating,
      tcIsChecked: true,
      ratingError: null,
      titleError: null,
      contentError: null,
      imageError: null,
      tcError: null,
      classVariableName: classVariableName,
    };
  },
  methods: {
    formatDate(val) {
      return formatDate(val);
    },

    isEmpty(val) {
      return isEmpty(val);
    },
    handleSubmit() {
      if (!this.validateFieldsSuccess()) return;
      this.onSubmit({
        title: isEmpty(this.title) ? null : this.title.trim(),
        content: isEmpty(this.content) ? null : this.content.trim(),
        rating: this.rating,
        images: this.images,
        anonymous: this.anonymous,
      });
    },

    validateFieldsSuccess() {
      let ratingCheckFail =
        this.ratingValidations &&
        !this.ratingValidations.hidden &&
        this.ratingValidations.required &&
        this.rating === 0;
      let titleCheckFail =
        this.titleValidations &&
        !this.titleValidations.hidden &&
          (
            (this.titleValidations.required && isEmpty(this.title)) ||
            (isEmpty(this.title) && !isEmpty(this.content)) ||
            (isEmpty(this.title) && !isEmpty(this.images))
          );
      let contentCheckFail =
        this.contentValidations &&
        !this.contentValidations.hidden &&
        this.contentValidations.required &&
        isEmpty(this.content);
      let imageCheckFail =
        this.imageValidations &&
        !this.imageValidations.hidden &&
        this.imageValidations.required &&
        isEmpty(this.images);
      if (
        ratingCheckFail ||
        titleCheckFail ||
        contentCheckFail ||
        imageCheckFail ||
        !this.tcIsChecked
      ) {
        if (ratingCheckFail) this.ratingError = "Rating is required!";
        if (titleCheckFail) this.titleError = "Please provide a title";
        if (contentCheckFail)
          this.contentError = "Please provide the description";
        if (imageCheckFail)
          this.imageError = "Please provide at least 1 image to support";
        if (!this.tcIsChecked)
          this.tcError = "Please agree to the policy and terms";
        return false;
      }
      return true;
    },
    removeImage(imgIndex) {
      this.imageError = null;
      let imgs = [...this.images];
      imgs.splice(imgIndex, 1);
      this.images = imgs;
      emitEvent(CUSTOM_EVENTS.WRITE_REVIEW_ADD_REMOVE_PHOTO, getEventData(true, 'write_review', this.product, { anonymous: this.anonymous, rating: this.rating }));
    },
    getImage(img) {
      let image_as_base64 = URL.createObjectURL(img);
      return image_as_base64;
    },
    handleImagePreview(e) {
      this.imageError = null;
      let image_as_files = e.target.files;
      let imgs = [];
      if (!isEmpty(this.images)) {
        imgs = [...this.images];
        emitEvent(CUSTOM_EVENTS.WRITE_REVIEW_ADD_MORE_PHOTO, getEventData(true, 'write_review', this.product, { anonymous: this.anonymous, rating: this.rating }));
      } else {
        emitEvent(CUSTOM_EVENTS.REVIEW_PAGE_UPLOAD_PHOTO, getEventData(true, 'write_review', this.product, {anonymous: this.anonymous,rating: this.rating}));
      }

      for (var i = 0; i < image_as_files.length; i++) {
        let file = image_as_files[i];
        let fileSizeKiloBytes = file.size / 1024;
        if (
          fileSizeKiloBytes >
          this.imageValidations.sizeLimitInMegaBytes * 1024
        ) {
          this.imageError = `Maximum File Size exceeds ${this.imageValidations.sizeLimitInMegaBytes}MB!`;
          setTimeout(() => {
            this.imageError = null;
          }, 3000);
          document.getElementById("imgInput").value = "";
          return;
        }
        
        imgs.push(file);
      }

      if (imgs.length > this.imageValidations.countLimit) {
        this.imageError = `Maximum ${this.imageValidations.countLimit} images are allowed on a review!`;
        setTimeout(() => {
          this.imageError = null;
        }, 3000);
        document.getElementById("imgInput").value = "";
        return;
      }

      document.getElementById("imgInput").value = "";
      this.images = imgs;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "../assets/App.scss";
</style>
